import { useEffect, useRef, useState } from 'react';
import { useCueDiagnostics } from '../../lib/cue/useCueDiagnostics';

// True for ~1s after an inbound Cue relay lands on ANY channel (the
// per-channel version is useActivityBlink). The newest timestamp across all
// channels is the signal; the value present at mount is ignored.
function useAnyActivityBlink(): boolean {
  const { lastActivityAt } = useCueDiagnostics();
  const latest = Math.max(0, ...Object.values(lastActivityAt));
  const [blinking, setBlinking] = useState(false);
  const seenRef = useRef(latest);

  useEffect(() => {
    if (latest === seenRef.current) return;
    seenRef.current = latest;
    setBlinking(true);
    const timer = setTimeout(() => setBlinking(false), 1000);
    return () => clearTimeout(timer);
  }, [latest]);

  return blinking;
}

// public/zmist-logo.svg inlined, so the green dot can glow while signals
// arrive and settle back to normal when they stop.
export function ZmistLogo({ className }: { className?: string }) {
  const live = useAnyActivityBlink();
  return (
    <svg viewBox="0 0 100 100" className={className} role="img" aria-label="Zmist">
      <defs>
        <linearGradient id="zmistLogoTeal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#35E0C2" />
          <stop offset="100%" stopColor="#1B8E7A" />
        </linearGradient>
      </defs>
      <circle cx="14" cy="50" r="5" fill="#93A2B0" opacity="0.25" />
      <circle cx="34" cy="50" r="6" fill="#93A2B0" opacity="0.4" />
      <circle cx="56" cy="50" r="7" fill="#93A2B0" opacity="0.55" />
      <circle
        cx="84"
        cy="50"
        r="12"
        fill="#35E0C2"
        style={{ opacity: live ? 0.6 : 0, filter: 'blur(6px)', transition: 'opacity 250ms ease-out', transformOrigin: '84px 50px', transform: live ? 'scale(1.5)' : 'scale(1)' }}
      />
      <circle
        cx="84"
        cy="50"
        r="12"
        fill="url(#zmistLogoTeal)"
        style={{ transition: 'transform 250ms ease-out', transformOrigin: '84px 50px', transform: live ? 'scale(1.12)' : 'scale(1)' }}
      />
    </svg>
  );
}
